import { useCallback, useEffect, useState } from "react";
import { AxiosError } from "axios";
import { useTranslation, Trans } from "react-i18next";
import { isToday } from "date-fns";

import { useRequestDispatch } from "store/requestHooks";
import { useRootDispatch, useRootSelector } from "store/hooks";

import {
  Dialog,
  BarItems,
  DialogProps,
  TranslationAnchor,
} from "components/common";
import { SliderPageWrapper } from "../../../layout/sliderPageWrapper/SliderPageWrapper";

import {
  createFilterProps,
  renderConditionalProp,
  isExpired,
  typeMapper,
  PAYMENT_METHOD_TYPE,
  convert24to12HourTime,
  determinePaymentMethodDisplayText,
} from "utility";

import { FilterProps } from "store/common";
import {
  activeAccountTypeConfig,
  selectedAccountInfo,
} from "store/account/account-selector";
import { clearSlide, updateSlide } from "store/common/common-slice";
import {
  FetchPendingPayments,
  fetchPendingPayments,
} from "store/paymentActivity/paymentActivity-action";
import {
  FetchPaymentActivityResult,
  PaymentActivity,
} from "store/paymentActivity/paymentActivity-state";
import { selectAllschedulePayments } from "store/schedulePayments/schedulePayments-slice";
import { SubmitAndPay, submitAndPay } from "store/payments/payments-action";

import classes from "./ReviewPayment.module.scss";
import { Disclaimer } from "components/extra/disclaimer/Disclaimer";
import { clearSlideoutNotifications } from "store/notification/notification-slice";
import { useAddNotification } from "hooks";

export interface ReviewPaymentProps {}

const quickbarItems: BarItems[] = [];

export const ReviewPayment: React.FC<ReviewPaymentProps> = () => {
  const { t } = useTranslation([
    "common",
    "payment",
    "payment_next",
    "payment_final",
  ]);

  if (quickbarItems.length === 0) {
    quickbarItems.push(
      {
        title: t("payment_next:payments_label"),
        isVisited: true,
        icon: "payments",
      },
      {
        title: t("payment_next:review_label"),
        isSelected: true,
        icon: "grading",
      },
      {
        title: t("payment_next:complete_label"),
        icon: "verified",
      },
    );
  }

  const dispatch = useRootDispatch();
  const requestDispatch = useRequestDispatch();
  const addNotification = useAddNotification();

  const [dialogProps, setDialogProps] = useState<DialogProps | null>(null);
  const [pendingTransaction, setPendingTransaction] = useState<boolean>(false);
  const [isPendingTransactionLoading, setIsPendingTransactionLoading] =
    useState<boolean>(false);

  const config = useRootSelector(activeAccountTypeConfig);
  const { user } = useRootSelector((state) => state.user);
  const { paymentCenter, client } = useRootSelector((state) => state.config);
  const accountInfo = useRootSelector(selectedAccountInfo);
  const schedulePayments = useRootSelector(selectAllschedulePayments);
  const { paymentData, meta } = useRootSelector((state) => state.payments);
  const [isDisabled, setDisabled] = useState<boolean>(false);

  useEffect(() => {
    if (
      paymentData != null &&
      paymentData.paymentMethod != null &&
      paymentData.paymentDate != null
    ) {
      if (
        isExpired(
          paymentData.paymentMethod.expirationDate,
          paymentData.paymentDate,
        )
      ) {
        setDisabled(true);
        addNotification({
          translatedMessages: [t("payment_next:card_expires_before")],
          type: "ERROR",
          closable: true,
        });
      }
    }
  }, [paymentData, addNotification, dispatch, t]);

  const backButtonHandler = () => {
    dispatch(clearSlideoutNotifications());
    dispatch(updateSlide({ id: "MakeAPayment" }));
  };

  const processPaymentHandler = useCallback(
    (googlePayload?: any, resolve?: any) => {
      requestDispatch<SubmitAndPay, PaymentActivity>(submitAndPay, {
        ...paymentData,
        ...googlePayload,
        userId: user?.id,
      })
        .unwrap()
        .then(() => {
          if (resolve) {
            resolve({
              transactionState: "SUCCESS",
            });
          }
          dispatch(updateSlide({ id: "MakeAPaymentConfirmation" }));
        })
        .catch(() => {
          if (typeof resolve === "function") {
            resolve({
              transactionState: "ERROR",
              error: {
                intent: "PAYMENT_AUTHORIZATION",
                message: t("payment_final:payment_retry"),
                reason: "PAYMENT_DATA_INVALID",
              },
            });
          }
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paymentData, user?.id],
  );

  useEffect(() => {
    const filters: FilterProps = {
      minDate: new Date().toDateString(),
      status: 3,
    };

    const filterProps = createFilterProps(filters);
    setIsPendingTransactionLoading(true);
    requestDispatch<FetchPendingPayments, FilterProps>(
      fetchPendingPayments,
      filterProps,
    )
      .unwrap()
      .then((res: FetchPaymentActivityResult) => {
        if (res.results.length > 0) {
          setPendingTransaction(true);
        }
      })
      .catch((err: AxiosError) => {
        //setIsPendingTransactionLoading(false)
      })
      .finally(() => {
        setIsPendingTransactionLoading(false); //need this any result
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pendingTrasactionsCancelAction = () => {
    setPendingTransaction(false);
    dispatch(clearSlide());
  };

  useEffect(() => {
    let maximumScheduledPaymentsAllowed = 6;
    if (
      paymentCenter.clientScheduledPayments != null &&
      paymentCenter.clientScheduledPayments.maximumScheduledPaymentsAllowed !=
        null
    ) {
      maximumScheduledPaymentsAllowed =
        paymentCenter.clientScheduledPayments.maximumScheduledPaymentsAllowed;
    }

    let mySchedulePayments = 0;
    let myRecurrencePayments = 0;
    //Only use payments with the scheduled status
    let currentScheduled = schedulePayments.filter((p) => p.status === 0);
    if (currentScheduled && currentScheduled.length > 0) {
      mySchedulePayments = currentScheduled.length;
      myRecurrencePayments = currentScheduled.filter((a) => {
        return a.recurrenceScheduleId !== null;
      }).length;
    }

    let todayFlag = false;
    if (
      paymentData?.paymentDate &&
      isToday(new Date(paymentData.paymentDate))
    ) {
      todayFlag = true;
    }

    if (maximumScheduledPaymentsAllowed === 1 && !todayFlag) {
      if (mySchedulePayments - myRecurrencePayments > 0) {
        setDialogProps({
          icon: "warning_amber",
          primaryActionHandler: pendingTrasactionsCancelAction,
          secondaryActionHandler: pendingTrasactionsCancelAction,
          showSecondaryAction: false,
          secondaryActionLabel: t("common:close"),
          type: "warning",
          closeIcon: false,
          scrimClickAction: true,
          children: t("payment_final:OnlyOneScheduledTransactionAllowed"),
        });
      }
    } else {
      if (pendingTransaction || mySchedulePayments > 0) {
        setDialogProps({
          icon: "warning_amber",
          primaryActionLabel: t("common:continue_label"),
          primaryActionHandler: () => setPendingTransaction(false),
          secondaryActionHandler: pendingTrasactionsCancelAction,
          showSecondaryAction: true,
          type: "warning",
          closeIcon: false,
          scrimClickAction: true,
          children: t("payment_final:ScheduledandorPendingTransaction"),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingTransaction]);

  return (
    <SliderPageWrapper
      quickbarItems={{ items: quickbarItems, progress: "0.6" }}
      headerTitle={t("common:make_payment_label")}
    >
      {accountInfo && paymentData ? (
        <div className={classes.Wrapper}>
          <p className={classes.Heading}>
            {t("payment_final:payment_details")}
          </p>
          <div className={classes.Container}>
            <div className={classes.ContainerItem}>
              <p>{t("payment_final:payment_amount")}</p>
              <p className={classes.ContainerPaymentAmount}>
                {t("common:currency_format", {
                  value: +paymentData?.paymentAmount!,
                })}
              </p>
            </div>
            {paymentData.feeTotal != null && paymentData.feeTotal > 0 ? (
              <div className={classes.ContainerItem}>
                <p>{t("payment_final:convenience_fee_label")}</p>
                <p className={classes.ContainerPaymentAmount}>
                  {t("common:currency_format", {
                    value: +paymentData.feeTotal!,
                  })}
                </p>
              </div>
            ) : null}
            <div className={classes.ContainerItem}>
              <p>{t("payment_final:total_payment")}</p>
              <p className={classes.ContainerPaymentAmount}>
                {t("common:currency_format", {
                  value:
                    (+paymentData.feeTotal! || 0) +
                    +paymentData?.paymentAmount!,
                })}
              </p>
            </div>
            <div className={classes.ContainerItem}>
              <p>{t("payment_final:payment_to")}</p>
              <p>{client.name}</p>
            </div>
            <div className={classes.ContainerItem}>
              <p>{t("payment_final:first_name_label")}</p>
              <p>{user?.givenName}</p>
            </div>
            <div className={classes.ContainerItem}>
              <p>{t("payment_final:last_name_label")}</p>
              <p>{user?.familyName}</p>
            </div>
            <div className={classes.ContainerItem}>
              <p>{t("payment:accountNumber")}</p>
              <p>{accountInfo.accountNumber}</p>
            </div>
            <div className={classes.ContainerItem}>
              <p>{t("payment_next:payment_type")}</p>
              <p>
                {paymentData.paymentType &&
                  config.paymentTypes.find(
                    (pt) => pt.code === paymentData?.paymentType,
                  )?.name}
              </p>
            </div>
            {/* 
              Might need to add a check here for if a schedule was selected
              or not and display a more appropriate label  
            */}
            <div className={classes.ContainerItem}>
              <p>{t("payment:paymentDate")}</p>
              <p>
                {t("common:date_format", {
                  value: paymentData?.paymentDate,
                })}
              </p>
            </div>
            {!!paymentData.paymentMethod &&
            !!paymentData.paymentMethod.paymentMethodType ? (
              <div className={classes.ContainerItem}>
                <p>{t("payment:paymentMethodName")}</p>
                <p>
                  {determinePaymentMethodDisplayText(
                    paymentData.paymentMethod.paymentMethodType,
                    paymentData.walletType,
                  )}
                </p>
              </div>
            ) : null}

            <ft-divider class={classes.Divider}></ft-divider>
            <Disclaimer
              hasBottomDivider={true}
              translatedText={
                <Trans
                  i18nKey="quickpay:disclaimer_text"
                  values={{
                    convenienceFee: paymentCenter.defaultFees.convenienceFee,
                  }}
                  components={{ a: <TranslationAnchor /> }}
                />
              }
            />
            {!!paymentData &&
            !!paymentData.paymentMethod &&
            paymentData.paymentMethod.paymentMethodType &&
            paymentData.paymentMethod.paymentMethodType !==
              PAYMENT_METHOD_TYPE.eWallet ? (
              <div className={classes.AuthAndRevoke}>
                <p>
                  {t(
                    `payment_final:submit-auth-confirmation_${typeMapper(
                      paymentData.paymentMethod.paymentMethodType,
                      "paymentMethodTypeReverse",
                    )}_auth`,
                    {
                      clientName: client.name,
                      contactPhone: client.primaryContactPhone,
                      cutoffTime: convert24to12HourTime(client.cutOffTime!),
                      timeZoneName: typeMapper(
                        client.timeZoneId!,
                        "TimeZoneIdReverse",
                      ),
                    },
                  )}
                </p>

                <p className={classes.AuthorizationAndRevocation}>
                  {t(
                    `payment_final:submit-auth-confirmation_${typeMapper(
                      paymentData.paymentMethod.paymentMethodType,
                      "paymentMethodTypeReverse",
                    )}_revoke`,
                    {
                      todaysDate: t("common:date_format", {
                        value: new Date().toISOString(),
                      }),
                      contactPhone: client.primaryContactPhone,
                    },
                  )}
                </p>
              </div>
            ) : (
              <></>
            )}
            <div className={classes.Buttons}>
              <ft-button
                onClick={backButtonHandler}
                class={[classes.Back, "outline-button"].join(" ")}
                label={t("common:back_label")}
                outlined
              ></ft-button>
              <ft-button
                class={classes.Make_payment}
                label={t("payment_final:submit_and_pay")}
                unelevated
                {...renderConditionalProp(
                  meta?.isLoading || isDisabled || false,
                  "disabled",
                )}
                data-testid="make-payment-review-submit-button"
                onClick={() => processPaymentHandler()}
              ></ft-button>
            </div>
          </div>
        </div>
      ) : null}
      {dialogProps && (
        <Dialog
          icon={dialogProps.icon}
          primaryActionLabel={dialogProps.primaryActionLabel}
          primaryActionHandler={dialogProps.primaryActionHandler}
          secondaryActionHandler={dialogProps.secondaryActionHandler}
          showSecondaryAction={dialogProps.showSecondaryAction}
          type={dialogProps.type}
          closeIcon={dialogProps.closeIcon}
          scrimClickAction={dialogProps.scrimClickAction}
        >
          {dialogProps.children}
        </Dialog>
      )}
      {(isPendingTransactionLoading || meta.isLoading) && (
        <ft-screen-loader></ft-screen-loader>
      )}
    </SliderPageWrapper>
  );
};
